export const MOBILE_MAX_WIDTH = 767;

export function isMobileViewport() {
	return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

export function getPageFromLink(anchor) {
	const url = new URL(anchor.href, window.location.origin);
	const queryPage = url.searchParams.get('paged');

	if (queryPage) {
		return Math.max(1, parseInt(queryPage, 10));
	}

	const pathMatch = url.pathname.match(/\/page\/(\d+)\/?$/);

	if (pathMatch) {
		return Math.max(1, parseInt(pathMatch[1], 10));
	}

	return 1;
}

export function syncUrlParams(url, params, defaultPerPage, extraDeletes = [], extraSets = () => {}) {
	url.searchParams.delete('s');
	url.searchParams.delete('posts_per_page');
	url.searchParams.delete('paged');

	extraDeletes.forEach((key) => url.searchParams.delete(key));

	if (params.s) {
		url.searchParams.set('s', params.s);
	}

	extraSets(url, params);

	if (params.posts_per_page && params.posts_per_page !== defaultPerPage) {
		url.searchParams.set('posts_per_page', params.posts_per_page);
	}

	if (params.paged && params.paged !== '1') {
		url.searchParams.set('paged', params.paged);
	}
}

export function readUrlListingState(defaultPerPage, { topicParam = null, topicStateKey = null, listParams = [] } = {}) {
	const url = new URL(window.location.href);
	const state = {
		s: url.searchParams.get('s') || '',
		posts_per_page: url.searchParams.get('posts_per_page') || defaultPerPage,
		paged: url.searchParams.get('paged') || '1',
	};

	const params = [...listParams];

	if (topicParam) {
		params.unshift({ param: topicParam, stateKey: topicStateKey || topicParam });
	}

	params.forEach(({ param, stateKey }) => {
		const raw = url.searchParams.get(param);
		const key = stateKey || param;
		state[key] = raw ? raw.split(',').map((slug) => slug.trim()).filter(Boolean) : [];
	});

	return state;
}

export function createMobileFiltersController(
	root,
	{ blockName, backdropSelector, toggleSelector, panelSelector, closeSelector, onWillClose = null }
) {
	const filtersPanel = root.querySelector(panelSelector);
	const filtersBackdrop = root.querySelector(backdropSelector);
	const filtersToggle = root.querySelector(toggleSelector);
	const filtersClose = closeSelector ? root.querySelector(closeSelector) : null;
	let filtersOpen = false;
	let lastFocused = null;

	const setDialogMode = (open) => {
		if (!filtersPanel || !isMobileViewport()) {
			return;
		}

		if (open) {
			filtersPanel.setAttribute('role', 'dialog');
			filtersPanel.setAttribute('aria-modal', 'true');
			lastFocused = document.activeElement;
			(filtersClose || filtersPanel.querySelector('input, button, select, textarea'))?.focus();
			return;
		}

		filtersPanel.removeAttribute('role');
		filtersPanel.removeAttribute('aria-modal');

		if (lastFocused?.focus) {
			lastFocused.focus();
		}

		lastFocused = null;
	};

	const setFiltersOpen = (open) => {
		if (!isMobileViewport()) {
			filtersOpen = false;
			root.classList.remove(`${blockName}--filters-open`);

			if (filtersBackdrop) {
				filtersBackdrop.hidden = true;
			}

			if (filtersToggle) {
				filtersToggle.setAttribute('aria-expanded', 'false');
			}

			if (filtersPanel) {
				filtersPanel.removeAttribute('role');
				filtersPanel.removeAttribute('aria-modal');
				filtersPanel.setAttribute('aria-hidden', 'false');
			}

			return;
		}

		filtersOpen = open;
		root.classList.toggle(`${blockName}--filters-open`, open);

		if (!open && isMobileViewport() && onWillClose) {
			onWillClose();
		}

		if (filtersBackdrop) {
			filtersBackdrop.hidden = !open;
		}

		if (filtersToggle) {
			filtersToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
		}

		if (filtersPanel) {
			filtersPanel.setAttribute('aria-hidden', open ? 'false' : 'true');
		}

		document.documentElement.classList.toggle(`${blockName}-filters-scroll-lock`, open);
		setDialogMode(open);
	};

	const syncFiltersAccessibility = () => {
		if (!filtersPanel) {
			return;
		}

		if (isMobileViewport()) {
			filtersPanel.setAttribute('aria-hidden', filtersOpen ? 'false' : 'true');
			return;
		}

		setFiltersOpen(false);
	};

	syncFiltersAccessibility();
	window.addEventListener('resize', syncFiltersAccessibility);

	const onKeydown = (event) => {
		if (event.key === 'Escape' && filtersOpen) {
			setFiltersOpen(false);
		}
	};

	document.addEventListener('keydown', onKeydown);

	return {
		filtersBackdrop,
		setFiltersOpen,
		isFiltersOpen: () => filtersOpen,
		destroy: () => {
			document.removeEventListener('keydown', onKeydown);
		},
	};
}

export function showFetchError(results, blockName, message) {
	if (!results) {
		return;
	}

	const className = `${blockName}__fetch-error`;
	let error = results.querySelector(`.${className}`);

	if (!error) {
		error = document.createElement('p');
		error.className = className;
		error.setAttribute('role', 'alert');
		results.prepend(error);
	}

	error.textContent = message;
	error.hidden = false;
}

export function clearFetchError(results, blockName) {
	const error = results?.querySelector(`.${blockName}__fetch-error`);

	if (error) {
		error.hidden = true;
	}
}
